import { Customer, TenantProfile } from "@hydrologiq/gql-experience/graphql"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import RunDry from "@hydrologiq/shared/figures/customer/RunDry"
import TimeToDepletion from "@hydrologiq/shared/figures/generic/TimeToDepletion"
import RefuelNeededIn from "@hydrologiq/shared/figures/generic/RefuelNeededIn"
import CustomerCO2e from "@hydrologiq/shared/figures/customer/CO2e"
import CustomerEnergy from "@hydrologiq/shared/figures/customer/Energy"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import CurrentOrder from "@hydrologiq/shared/figures/customer/CurrentOrder"
import CustomerOnSite from "@hydrologiq/shared/figures/customer/OnSite"
import Alerts from "@hydrologiq/shared/figures/customer/Alerts"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
interface CustomerFiguresProps {
  customer?: Customer
  loading: boolean
  error: string | undefined
}

enum CustomerFigure {
  Orders = "orders",
  Alerts = "alerts",
  RunDry = "run_dry", // Legacy - for VMI
  RefuelNeededIn = "refuel_needed_in", // Installation-level for Rental
  TimeToDepletion = "time_to_depletion", // Asset-level for Rental and VMI
  HydrogenLevel = "hydrogen_level",
  CO2e = "co2e",
  Energy = "energy",
}

const getCustomerFigures = (profile: TenantProfile) => {
  const defaultFigures = [
    CustomerFigure.Orders,
    CustomerFigure.Alerts,
    CustomerFigure.TimeToDepletion, // VMI only shows Asset Depleted in
    CustomerFigure.HydrogenLevel,
  ]
  switch (profile) {
    case TenantProfile.Rental:
      return [
        CustomerFigure.Orders,
        CustomerFigure.Alerts,
        CustomerFigure.RefuelNeededIn, // Installation-level run_dry
        CustomerFigure.TimeToDepletion, // Asset-level run_dry
        CustomerFigure.CO2e,
        CustomerFigure.Energy,
      ]
    case TenantProfile.FuelProducer:
      return defaultFigures
    default:
      return defaultFigures
  }
}
function CustomerFigures({ customer, loading, error }: CustomerFiguresProps) {
  const profile = useGetProfile()
  const figures = getCustomerFigures(profile)
  const assetDepletionEnabled = useGetFeature("asset_depletion")
  return (
    <FigureGrid loading={loading}>
      {figures.includes(CustomerFigure.Orders) && <CurrentOrder context={customer} loading={loading} error={error} />}
      {figures.includes(CustomerFigure.Alerts) && (
        <Alerts alerts={customer?.alertCount ?? undefined} loading={loading} error={error} />
      )}
      {figures.includes(CustomerFigure.RefuelNeededIn) && (
        <RefuelNeededIn
          heading="Refuel needed in"
          minutes={customer?.earliestInstallationRunDryMinutes ?? undefined}
          location={customer?.earliestInstallationRunDrySite?.name}
          system={customer?.earliestInstallationRunDrySystem?.name}
          systemId={customer?.earliestInstallationRunDrySystem?.id}
          granularity={"hours"}
          loading={loading}
          error={error}
          isPlaceholder={!customer?.earliestInstallationRunDryMinutes}
        />
      )}
      {assetDepletionEnabled && figures.includes(CustomerFigure.TimeToDepletion) && (
        <TimeToDepletion
          heading="Asset Depleted in"
          minutes={customer?.earliestRunDryMinutes ?? undefined}
          location={customer?.earliestRunDrySite?.name}
          site={customer?.earliestRunDrySite?.id}
          granularity={"hours"}
          loading={loading}
          error={error}
          isPlaceholder={!customer?.earliestRunDryMinutes}
          customer={customer?.id}
        />
      )}
      {figures.includes(CustomerFigure.RunDry) && <RunDry context={customer} loading={loading} error={error} />}
      {figures.includes(CustomerFigure.HydrogenLevel) && (
        <CustomerOnSite
          prefix="Hydrogen"
          current={customer?.totalHydrogenOnSiteKg ?? undefined}
          loading={loading}
          error={error}
          mutliple={customer?.assetsOnHireCount ? customer.assetsOnHireCount > 1 : false}
        />
      )}
      {figures.includes(CustomerFigure.CO2e) && <CustomerCO2e context={customer} loading={loading} error={error} />}
      {figures.includes(CustomerFigure.Energy) && <CustomerEnergy context={customer} loading={loading} error={error} />}
    </FigureGrid>
  )
}

export default CustomerFigures
